import React, {
	useCallback,
	useMemo,
	useRef,
	type ReactNode,
	type MouseEvent,
	type Ref,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { Divider, MenuSection } from '@atlaskit/side-nav-items/menu-section';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { CUSTOM_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	APP,
	APP_SUBPAGE,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	LEVEL_ONE,
	LEVEL_TWO,
	LEVEL_THREE,
	type Level,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_APPS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { toMenuIdFromMenuIdList } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/menu-id/index.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { ReorderActionsRenderer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/reorder-actions-renderer/ReorderActionsRenderer.tsx';
import { useNestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop/nested-item-drag-and-drop';
import { expVal } from '@atlassian/jira-feature-experiments';
import { EnvLozenge } from '../env-lozenge/EnvLozenge.tsx';
import { AppIcon } from '../app-icon/AppIcon.tsx';
import { AppActions } from '../app-actions/AppActions.tsx';
import type { AppSection } from '../../types.tsx';
import { useAppReordering } from '../../utils/useAppReordering.tsx';
import type {
	Nav4AppMenuItem$key,
	Nav4AppMenuItem$data,
} from './__generated__/Nav4AppMenuItem.graphql';

type Nav4AppMenuItemProps = {
	queryRef: Nav4AppMenuItem$key;
	onData?: <T extends Nav4AppMenuItem$data>(data: T) => T;
	onClick?: OnClick;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

type OnClick = (
	e: MouseEvent,
	target: HTMLAnchorElement | null,
	styleClass: string,
	url: string | null,
) => void;

export function Nav4AppMenuItem({
	queryRef,
	onData,
	onClick,
	currentItems,
	currentOrder,
	item,
}: Nav4AppMenuItemProps): React.JSX.Element {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const rawData = useFragment<Nav4AppMenuItem$key>(
		graphql`
			fragment Nav4AppMenuItem on JiraAppNavigationItem {
				appId
				envLabel
				iconUrl
				label
				styleClass
				sections {
					label
					links {
						label
						url
						styleClass
					}
				}
				settingsUrl
				url
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const data = useMemo(
		() => (onData ? onData(normalizeData(rawData)) : normalizeData(rawData)),
		[onData, rawData],
	);

	const icon = useMemo(
		() => (
			<AppIcon
				shouldChangeFillColor={!fg('jira-nav-addon-icon-disable-color-filter')}
				iconUrl={data.iconUrl || ''}
			/>
		),
		[data.iconUrl],
	);
	const withSeparators = useMemo(() => shouldUseSeparators(data), [data]);
	const envLozenge = useMemo(() => <EnvLozenge label={data.envLabel || ''} />, [data.envLabel]);

	let reorderActions: ((onClose: () => void) => React.ReactNode) | undefined;
	if (
		currentItems &&
		currentOrder &&
		item &&
		expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)
	) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { entityAri, ownerAri } = useAppReordering();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		reorderActions = useCallback(
			(onClose: () => void) => (
				<ReorderActionsRenderer
					currentItems={currentItems}
					currentOrder={currentOrder}
					entityAri={entityAri}
					ownerAri={ownerAri}
					fireAnalyticsEvent={undefined}
					item={item}
					onClose={onClose}
				/>
			),
			[currentItems, currentOrder, entityAri, item, ownerAri],
		);
	}

	const actionsOnHover = useMemo(
		() => (
			<AppActions
				settingsUrl={data.settingsUrl || ''}
				appId={data.appId || ''}
				reorderActions={reorderActions}
			/>
		),
		[data.settingsUrl, data.appId, reorderActions],
	);

	const expandable = useMemo(() => {
		if ((data.sections || []).length === 0) {
			return null;
		}
		return (
			<MaybeExpandable
				actionsOnHover={actionsOnHover}
				appId={data.appId || ''}
				envLozenge={envLozenge}
				icon={icon}
				label={data.label || 'Unknown app'} // This should never be the case though
				// oxlint-disable-next-line typescript/consistent-type-assertions
				menuIds={toExpandableMenuId(data.sections as unknown as AppSection[])}
				itemId={data.appId || ''}
				level={LEVEL_ONE}
			>
				{(data.sections || []).map((section, sectionIndex) =>
					(section?.links || []).length ? (
						<MenuListItem key={`${sectionIndex}-${section?.label}`}>
							<MenuSection>
								{withSeparators && sectionIndex > 0 ? <Divider /> : null}
								<MenuList>
									<MaybeExpandable
										appId={data.appId || ''}
										label={section?.label || ''}
										// oxlint-disable-next-line typescript/consistent-type-assertions
										menuIds={toExpandableMenuId([
											{ links: section?.links },
										] as unknown as AppSection[])}
										itemId={`section/${sectionIndex}`}
										level={LEVEL_TWO}
									>
										{(section?.links || []).map((link, linkIndex) => (
											<SimpleItem
												appId={data.appId || ''}
												key={link?.url || ''}
												label={link?.label || ''}
												onClick={onClick}
												styleClass={link?.styleClass || ''}
												url={link?.url == null ? null : removeTrailingSlash(link?.url)}
												itemId={`section/${sectionIndex}/link/${linkIndex}`}
												level={section?.label ? LEVEL_THREE : LEVEL_TWO}
											/>
										))}
									</MaybeExpandable>
								</MenuList>
							</MenuSection>
						</MenuListItem>
					) : null,
				)}
			</MaybeExpandable>
		);
	}, [
		data.sections,
		data.label,
		data.appId,
		icon,
		envLozenge,
		actionsOnHover,
		withSeparators,
		onClick,
	]);

	if (expandable) {
		return expandable;
	}

	return (
		<SimpleItem
			actionsOnHover={actionsOnHover}
			appId={data.appId || ''}
			envLozenge={envLozenge}
			icon={icon}
			label={data.label || 'Unknown app'} // This should never be the case
			onClick={onClick}
			styleClass={data.styleClass || ''}
			url={data.url == null ? null : removeTrailingSlash(data.url || '')}
			itemId={data.appId || ''}
			level={LEVEL_ONE}
		/>
	);
}

function SimpleItem({
	actionsOnHover,
	appId,
	envLozenge,
	icon,
	label,
	onClick,
	styleClass,
	url,
	itemId,
	level,
}: {
	actionsOnHover?: ReactNode;
	appId: string;
	envLozenge?: ReactNode;
	icon?: JSX.Element;
	label: string;
	onClick?: OnClick;
	styleClass?: string;
	// It may be the case there are Connect pages without URLs that open modals etc
	// And in the click handler we do check for the null values, so it is better we just keep it this way
	url: string | null;
	itemId: string;
	level: Level;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const anchorElement = useRef<HTMLAnchorElement>(null);

	const onItemClick = useCallback(
		(e: MouseEvent) => {
			fireAppClickEvent({
				event: createAnalyticsEvent({}),
				itemId,
				appId,
				level,
			});

			onClick?.(e, anchorElement.current, styleClass || '', url);
		},
		[url, onClick, itemId, appId, level, styleClass, createAnalyticsEvent],
	);

	let dragAndDropState;
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { entityAri, ownerAri, group } = useAppReordering();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		dragAndDropState = useNestedItemDragAndDrop({
			entityAri,
			ownerAri,
			data: {
				id: appId.toString(),
				label,
			},
			group,
			item: undefined,
			isExpandable: false,
			isExpandOrFlyoutOpen: false,
			id: appId.toString(),
			isDragAndDropEnabled: true,
			onExpandFlyoutToggle: () => {
				// No op for dashboard menu item
			},
		});
	}

	const draggableRef: Ref<HTMLAnchorElement> = dragAndDropState?.draggableRef || null;

	// If a Connect app does NOT have a URL, we should use a MenuButtonItem instead.
	return (
		<Nav4MenuLinkItem
			href={removeTrailingSlash(url ?? '')}
			elemBefore={icon}
			elemAfter={envLozenge}
			actionsOnHover={actionsOnHover}
			menuId={toMenuItemMenuId(url ?? '')}
			ref={mergeRefs(anchorElement, draggableRef)}
			onClick={onItemClick}
			visualContentRef={dragAndDropState?.dropTargetRef}
			dropIndicator={dragAndDropState?.dropIndicator}
			isDragging={dragAndDropState?.isDragging}
			hasDragIndicator={dragAndDropState === undefined ? false : dragAndDropState.isItemDraggable}
		>
			{label}
		</Nav4MenuLinkItem>
	);
}

function MaybeExpandable(params: {
	actionsOnHover?: ReactNode;
	children: ReactNode;
	envLozenge?: ReactNode;
	icon?: JSX.Element;
	label: string;
	menuIds: string[];
	itemId: string;
	appId: string;
	level: Level;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { actionsOnHover, children, envLozenge, icon, label, menuIds, itemId, appId, level } =
		params;
	const menuId = toMenuIdFromMenuIdList(menuIds);
	const onClick = useCallback(() => {
		fireAppClickEvent({
			event: createAnalyticsEvent({}),
			itemId,
			appId,
			level,
			isExpandable: true,
		});
	}, [createAnalyticsEvent, itemId, appId, level]);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) onClick();
		},
		[onClick],
	);
	return label ? (
		<Nav4ExpandableMenuItem
			menuId={menuId}
			isExpanded={level !== LEVEL_ONE}
			onExpansionToggle={onExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				elemBefore={icon}
				elemAfter={envLozenge}
				testId={getTestId(menuId)}
				interactionName="nav4-sidebar-apps"
			>
				{label}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>{children}</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	) : (
		<>{children}</>
	);
}

function fireAppClickEvent(params: {
	event: UIAnalyticsEvent;
	itemId: string;
	appId: string;
	level: Level;
	isExpandable?: boolean;
}) {
	const { event, itemId, appId, level, isExpandable } = params;
	fireUIAnalytics(event, {
		action: 'clicked',
		actionSubject: 'navigationItem',
		actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_APPS],
		attributes: {
			level,
			componentType: CUSTOM_ITEM,
			itemId,
			isExpandable,
			section: 'yourApps',
			...(level === LEVEL_ONE
				? {
						itemType: APP,
					}
				: {
						itemType: APP_SUBPAGE,
						parentItemId: appId,
						parentItemType: APP,
					}),
		},
	});
}

function shouldUseSeparators(data: Nav4AppMenuItem$data) {
	for (const section of data.sections || []) {
		if (section?.label) return false;
	}
	return true;
}

/**
 * Common data transformer.
 * Filters out sections with empty links.
 */
function normalizeData(data: Nav4AppMenuItem$data) {
	return {
		...data,
		sections: (data.sections || []).filter((section) => (section?.links || []).length > 0),
	};
}

const removeTrailingSlash = (url = '') => url.replace(/\/$/, '');
const removeQueryParams = (url: string) => url.split('?')[0];
const removeHash = (url: string) => url.split('#')[0];

// Since the underlying mechanism operates only on the path we must trim all but the pathname.
export const toMenuItemMenuId = (path_ = ''): string => {
	let path = path_;
	// This is the format we ask Connect vendors to use: /plugins/servlet/ac/:addonKey/:moduleKey
	// However, there are Connect apps that do not follow our recommendations and add extra path segments.
	// As result they might look like this: /plugins/servlet/ac/:addonKey/:moduleKey/:extraPathSegment
	// Because of that we have issues with menu ids matching since the path created out of current route differs from the actual url.
	// To fix this we remove those extra path segments from the url.
	if (path.startsWith('/plugins/servlet/ac/')) {
		path = path.split('/').slice(0, 6).join('/');
	}
	return ENTITY_ID.APP(removeTrailingSlash(removeQueryParams(removeHash(path))));
};

// Because of the apps nature, the parent url may differ from the child url.
// And if we want to make sure the expand state is properly calculated, we must pass down children menu ids.
const toExpandableMenuId = (sections?: AppSection[]) =>
	(sections || []).flatMap(({ links }) => (links || []).map((link) => toMenuItemMenuId(link.url)));
